import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from './auth';

export interface TutorialData {
  operationsTypes: { name: string; isSelected: boolean }[];
  locations: { name: string; isSelected: boolean }[];
  radioChannels: { name: string; isSelected: boolean }[];
  tasks: { name: string; isSelected: boolean }[];
}

export class TutorialManager {
  private static instance: TutorialManager;

  private constructor() {}

  static getInstance(): TutorialManager {
    if (!TutorialManager.instance) {
      TutorialManager.instance = new TutorialManager();
    }
    return TutorialManager.instance;
  }

  /**
   * Prüft ob das Tutorial für die aktuelle Organisation benötigt wird
   */
  async checkIfTutorialNeeded(): Promise<boolean> {
    try {
      // Hole die aktuelle Organisation aus dem selectedOrgID (primär) oder Access-Token (fallback)
      let orgID: number | null = null;
      
      // 1. Versuche selectedOrgID aus localStorage zu holen (aktuelle Benutzerauswahl)
      const selectedOrgID = localStorage.getItem('selectedOrgID');
      if (selectedOrgID) {
        const parsedSelectedOrgID = parseInt(selectedOrgID);
        if (!isNaN(parsedSelectedOrgID)) {
          orgID = parsedSelectedOrgID;
          console.log('🏢 Tutorial-Check: Verwende selectedOrgID aus localStorage:', orgID);
        }
      }
      
      // 2. Fallback: Hole orgID aus dem Access-Token
      if (!orgID) {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
            console.log('🏢 Tutorial-Check: Verwende orgID aus JWT:', orgID);
          } catch (error) {
            console.error('❌ Fehler beim Parsen des Access-Tokens:', error);
          }
        }
      }

      if (!orgID) {
        console.error('❌ Keine Organisation gefunden - kann Tutorial-Check nicht durchführen');
        return false;
      }

      console.log('🏢 Tutorial-Check für Organisation:', orgID);

      // Prüfe ob bereits Daten vorhanden sind (nur für die aktuelle Organisation)
      const [operationsTypes, locations, radioChannels, tasks] = await Promise.all([
        this.fetchOperationsTypes(orgID),
        this.fetchLocations(orgID),
        this.fetchRadioChannels(orgID),
        this.fetchTasks(orgID)
      ]);

      // Wenn alle Kategorien leer sind, wird das Tutorial benötigt
      const needsTutorial = operationsTypes.length === 0 && 
                           locations.length === 0 && 
                           radioChannels.length === 0 && 
                           tasks.length === 0;

      console.log('🔍 Tutorial-Check für Organisation', orgID, ':', {
        operationsTypes: operationsTypes.length,
        locations: locations.length,
        radioChannels: radioChannels.length,
        tasks: tasks.length,
        needsTutorial
      });

      // Zusätzliche Debug-Ausgabe
      if (needsTutorial) {
        console.log('🎯 TUTORIAL WIRD BENÖTIGT! Alle Kategorien sind leer für Org', orgID);
        
        // Wenn das Tutorial benötigt wird, setze den Tutorial-State zurück
        this.forceResetTutorialState();
      } else {
        console.log('✅ Tutorial nicht benötigt - mindestens eine Kategorie hat Daten für Org', orgID);
      }

      return needsTutorial;
    } catch (error) {
      console.error('❌ Fehler beim Tutorial-Check:', error);
      // Bei Fehlern Tutorial anzeigen
      return true;
    }
  }

  /**
   * Setzt den Tutorial-State zurück, wenn das Tutorial benötigt wird
   */
  private forceResetTutorialState(): void {
    try {
      console.log('🔄 Erzwinge Tutorial-State-Reset für neuen Start');
      
      // Lösche den Tutorial-State aus localStorage
      localStorage.removeItem('tutorialState');
      localStorage.removeItem('tutorialCompleted');
      
      console.log('✅ Tutorial-State erfolgreich zurückgesetzt');
    } catch (error) {
      console.error('❌ Fehler beim Zurücksetzen des Tutorial-States:', error);
    }
  }

  /**
   * Speichert die Tutorial-Daten in der API
   */
  async saveTutorialData(data: TutorialData): Promise<boolean> {
    try {
      console.log('💾 Speichere Tutorial-Daten...');

      // Hole die aktuelle Organisation aus dem selectedOrgID (primär) oder Access-Token (fallback)
      let orgID: number | null = null;
      
      // 1. Versuche selectedOrgID aus localStorage zu holen (aktuelle Benutzerauswahl)
      const selectedOrgID = localStorage.getItem('selectedOrgID');
      if (selectedOrgID) {
        const parsedSelectedOrgID = parseInt(selectedOrgID);
        if (!isNaN(parsedSelectedOrgID)) {
          orgID = parsedSelectedOrgID;
          console.log('💾 Speichere Tutorial-Daten für selectedOrgID:', orgID);
        }
      }
      
      // 2. Fallback: Hole orgID aus dem Access-Token
      if (!orgID) {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
            console.log('💾 Speichere Tutorial-Daten für orgID aus JWT:', orgID);
          } catch (error) {
            console.error('❌ Fehler beim Parsen des Access-Tokens:', error);
          }
        }
      }

      if (!orgID) {
        console.error('❌ Keine Organisation gefunden - kann Tutorial-Daten nicht speichern');
        return false;
      }

      console.log('🏢 Speichere Tutorial-Daten für Organisation:', orgID);

      // Speichere nur ausgewählte Items
      const selectedLocations = data.locations.filter(item => item.isSelected);
      const selectedRadioChannels = data.radioChannels.filter(item => item.isSelected);
      const selectedTasks = data.tasks.filter(item => item.isSelected);

      // Operations Types werden anders behandelt - überspringe für den Moment
      console.log('ℹ️ Operations Types werden nicht gespeichert (benötigen andere Logik)');

      // Importiere die GLEICHEN Funktionen wie im globalen Einstellungsmodal
      const { 
        addLocationWithCacheClear, 
        addRadioChannelWithCacheClear, 
        addTaskWithCacheClear
      } = await import('./syncThrottled');

      // Speichere Locations (verwende die GLEICHE Funktion wie im Einstellungsmodal)
      for (const location of selectedLocations) {
        const success = await addLocationWithCacheClear(location.name);
        if (success) {
          console.log('✅ Location erfolgreich gespeichert:', location.name);
        } else {
          console.error('❌ Fehler beim Speichern der Location:', location.name);
        }
      }

      // Speichere RadioChannels (verwende die GLEICHE Funktion wie im Einstellungsmodal)
      for (const channel of selectedRadioChannels) {
        const success = await addRadioChannelWithCacheClear(channel.name);
        if (success) {
          console.log('✅ Radio Channel erfolgreich gespeichert:', channel.name);
        } else {
          console.error('❌ Fehler beim Speichern des RadioChannels:', channel.name);
        }
      }

      // Speichere Tasks (verwende die GLEICHE Funktion wie im Einstellungsmodal)
      for (const task of selectedTasks) {
        const success = await addTaskWithCacheClear(task.name);
        if (success) {
          console.log('✅ Task erfolgreich gespeichert:', task.name);
        } else {
          console.error('❌ Fehler beim Speichern des Tasks:', task.name);
        }
      }

      console.log('✅ Tutorial-Daten erfolgreich gespeichert');
      return true;
    } catch (error) {
      console.error('❌ Fehler beim Speichern der Tutorial-Daten:', error);
      return false;
    }
  }

  public async fetchOperationsTypes(orgID: number): Promise<any[]> {
    try {
      // Verwende den gleichen Endpoint wie syncThrottled
      const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_BY_ORG), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgID })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📡 Operations Types für Org geladen:', data);
        return data;
      }
      return [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Operations Types:', error);
      return [];
    }
  }

  public async fetchLocations(orgID: number): Promise<any[]> {
    try {
      // Verwende den gleichen Endpoint wie syncThrottled
      const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOCATIONS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgID: [orgID] })  // API erwartet Array
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📡 Locations für Org geladen:', data);
        return data;
      }
      return [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Locations:', error);
      return [];
    }
  }

  public async fetchRadioChannels(orgID: number): Promise<any[]> {
    try {
      // Verwende den gleichen Endpoint wie syncThrottled
      const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.RADIOCHANNELS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgID: [orgID] })  // API erwartet Array
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📡 Radio Channels für Org geladen:', data);
        return data;
      }
      return [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Radio Channels:', error);
      return [];
    }
  }

  public async fetchTasks(orgID: number): Promise<any[]> {
    try {
      // Verwende den gleichen Endpoint wie syncThrottled
      const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TASKS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgID })  // API erwartet einzelnen Wert
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📡 Tasks für Org geladen:', data);
        return data;
      }
      return [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Tasks:', error);
      return [];
    }
  }

  private async saveOperationsType(name: string, orgID: number): Promise<void> {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_ADD), {
      method: 'POST',
      body: JSON.stringify({ name, orgID })
    });
    
    if (!response.ok) {
      throw new Error(`Fehler beim Speichern des OperationsTypes: ${name}`);
    }
  }

  private async saveLocation(name: string, orgID: number): Promise<void> {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOCATIONS_ADD), {
      method: 'POST',
      body: JSON.stringify({ name, orgID })
    });
    
    if (!response.ok) {
      throw new Error(`Fehler beim Speichern der Location: ${name}`);
    }
  }

  private async saveRadioChannel(name: string, orgID: number): Promise<void> {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.RADIOCHANNELS_ADD), {
      method: 'POST',
      body: JSON.stringify({ name, orgID })
    });
    
    if (!response.ok) {
      throw new Error(`Fehler beim Speichern des RadioChannels: ${name}`);
    }
      }

  private async saveTask(name: string, orgID: number): Promise<void> {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TASKS_ADD), {
      method: 'POST',
      body: JSON.stringify({ name, orgID })
    });
    
    if (!response.ok) {
      throw new Error(`Fehler beim Speichern der Task: ${name}`);
    }
  }
}

export const getTutorialManager = (): TutorialManager => {
  return TutorialManager.getInstance();
};
